import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  increment,
  serverTimestamp,
  where,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  ArrowLeft, 
  Users, 
  Crown,
  Shield,
  Send,
  Image as ImageIcon,
  Heart,
  MessageCircle,
  MoreVertical,
  UserPlus,
  Settings,
  X,
  Trash2,
  Flag,
  Info
} from 'lucide-react';
import { Group, GroupMembership, GroupPost, PublicProfile } from '../../types/community';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';

export const GroupView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [group, setGroup] = useState<Group | null>(null);
  const [membership, setMembership] = useState<GroupMembership | null>(null);
  const [posts, setPosts] = useState<GroupPost[]>([]);
  const [members, setMembers] = useState<GroupMembership[]>([]);
  const [memberProfiles, setMemberProfiles] = useState<Record<string, PublicProfile>>({});
  const [userProfile, setUserProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'feed' | 'about' | 'members'>('feed');
  const [showPostMenu, setShowPostMenu] = useState<string | null>(null);
  const [showInfoSheet, setShowInfoSheet] = useState(false);
  const [showMembersSheet, setShowMembersSheet] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch group data
  const fetchGroupData = useCallback(async () => {
    if (!id || !user) return;

    try {
      setLoading(true);

      // Fetch group
      const groupDoc = await getDoc(doc(db, 'groups', id));
      if (!groupDoc.exists()) {
        setGroup(null);
        setLoading(false);
        return;
      }

      const groupData = {
        id: groupDoc.id,
        ...groupDoc.data(),
        createdAt: groupDoc.data()?.createdAt?.toDate?.() ?? null,
        updatedAt: groupDoc.data()?.updatedAt?.toDate?.() ?? null,
        lastActivity: groupDoc.data()?.lastActivity?.toDate?.() ?? null,
      } as Group;

      setGroup(groupData);

      // Fetch user's membership
      const membershipQuery = query(
        collection(db, 'groupMemberships'),
        where('groupId', '==', id),
        where('userId', '==', user.uid)
      );
      const membershipSnap = await getDocs(membershipQuery);
      
      if (!membershipSnap.empty) {
        const membershipData = {
          id: membershipSnap.docs[0].id,
          ...membershipSnap.docs[0].data(),
          joinedAt: membershipSnap.docs[0].data()?.joinedAt?.toDate?.() ?? null,
        } as GroupMembership;
        setMembership(membershipData);
      }

      // Fetch posts
      const postsQuery = query(
        collection(db, 'groupPosts'),
        where('groupId', '==', id),
        orderBy('createdAt', 'desc')
      );
      const postsSnap = await getDocs(postsQuery);
      const postsData = postsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data()?.createdAt?.toDate?.() ?? null,
        updatedAt: doc.data()?.updatedAt?.toDate?.() ?? null,
      })) as GroupPost[];
      setPosts(postsData);

      // Fetch members
      const membersQuery = query(
        collection(db, 'groupMemberships'),
        where('groupId', '==', id)
      );
      const membersSnap = await getDocs(membersQuery);
      const membersData = membersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        joinedAt: doc.data()?.joinedAt?.toDate?.() ?? null,
      })) as GroupMembership[];
      setMembers(membersData);

      // Fetch member profiles
      const uniqueUserIds = [...new Set([
        ...postsData.map(p => p.authorId),
        ...membersData.map(m => m.userId)
      ])];

      const profilePromises = uniqueUserIds.map(async (userId) => {
        try {
          const profileDoc = await getDoc(doc(db, 'publicProfiles', userId));
          if (profileDoc.exists()) {
            return {
              uid: profileDoc.id,
              ...profileDoc.data(),
              joinedDate: profileDoc.data()?.joinedDate?.toDate?.() ?? null,
            } as PublicProfile;
          }
          return null;
        } catch (error) {
          console.error(`Error fetching profile for ${userId}:`, error);
          return null;
        }
      });

      const profiles = await Promise.all(profilePromises);
      const profileMap: Record<string, PublicProfile> = {};
      profiles.forEach(profile => {
        if (profile) {
          profileMap[profile.uid] = profile;
        }
      });
      setMemberProfiles(profileMap);

      // Fetch user's profile
      if (user.uid) {
        const userProfileDoc = await getDoc(doc(db, 'publicProfiles', user.uid));
        if (userProfileDoc.exists()) {
          setUserProfile({
            uid: userProfileDoc.id,
            ...userProfileDoc.data(),
            joinedDate: userProfileDoc.data()?.joinedDate?.toDate?.() ?? null,
          } as PublicProfile);
        }
      }

    } catch (error) {
      console.error('Error fetching group data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchGroupData();
    setRefreshing(false);
  };

  // Join group
  const joinGroup = async () => {
    if (!user || !id || !group) return;

    try {
      const membershipData = {
        userId: user.uid,
        groupId: id,
        role: 'member' as const,
        canPost: group.allowMemberPosts ?? true,
        canInvite: group.allowMemberInvites ?? true,
        joinedAt: serverTimestamp(),
        status: 'active' as const,
      };

      await addDoc(collection(db, 'groupMemberships'), membershipData);
      await updateDoc(doc(db, 'groups', id), {
        memberCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      await fetchGroupData();
    } catch (error) {
      console.error('Error joining group:', error);
      alert('Failed to join group. Please try again.');
    }
  };

  // Leave group
  const leaveGroup = async () => {
    if (!membership || !id) return;
    
    if (!confirm('Are you sure you want to leave this group?')) return;

    try {
      await deleteDoc(doc(db, 'groupMemberships', membership.id));
      await updateDoc(doc(db, 'groups', id), {
        memberCount: increment(-1),
        updatedAt: serverTimestamp(),
      });

      navigate('/community');
    } catch (error) {
      console.error('Error leaving group:', error);
      alert('Failed to leave group. Please try again.');
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Create post
  const handleCreatePost = async () => {
    if (!user || !id || !group || !newPost.trim()) return;

    try {
      setPosting(true);

      let imageUrl: string | undefined;

      // Upload image if selected
      if (selectedFile) {
        const imageRef = ref(storage, `groupPosts/${id}/${Date.now()}_${selectedFile.name}`);
        await uploadBytes(imageRef, selectedFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Create post
      const postData = {
        groupId: id,
        authorId: user.uid,
        content: newPost.trim(),
        imageUrl: imageUrl || null,
        type: 'text' as const,
        likeCount: 0,
        commentCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'groupPosts'), postData);

      // Update group last activity
      await updateDoc(doc(db, 'groups', id), {
        lastActivity: serverTimestamp(),
        postCount: increment(1),
      });

      // Clear form
      setNewPost('');
      setSelectedFile(null);
      setImagePreview(null);

      // Refresh posts
      await fetchGroupData();

      // Scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  // Like post
  const handleLikePost = async (postId: string) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'groupPosts', postId), {
        likeCount: increment(1),
      });

      // Update local state
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, likeCount: (post.likeCount || 0) + 1 }
            : post
        )
      );
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Delete post
  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await deleteDoc(doc(db, 'groupPosts', postId));
      await updateDoc(doc(db, 'groups', id!), {
        postCount: increment(-1),
      });

      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
      setShowPostMenu(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  // Get author display
  const getAuthorDisplay = (authorId: string) => {
    const profile = memberProfiles[authorId];
    if (!profile) return { name: 'Anonymous', avatar: null };

    return {
      name: profile.showRealName ? profile.displayName : 'Anonymous Hero',
      avatar: profile.profilePicture || null,
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Users className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 font-medium">Loading Group...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Group Not Found</h2>
          <button
            onClick={() => navigate('/community')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
          >
            Back to Community
          </button>
        </div>
      </div>
    );
  }

  const isLeader = membership?.role === 'leader';
  const isAdmin = membership?.role === 'leader' || membership?.role === 'co_admin';
  const isMember = !!membership;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <button
                onClick={() => navigate('/community')}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-gray-900 truncate">{group.name}</h1>
                <p className="text-xs text-gray-600">{group.memberCount} members</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!isMember ? (
                <button
                  onClick={joinGroup}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all"
                >
                  Join
                </button>
              ) : (
                <>
                  {isAdmin && (
                    <button
                      onClick={() => setShowInfoSheet(true)}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <Settings className="w-5 h-5 text-gray-600" />
                    </button>
                  )}
                  {membership && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded-lg">
                      {isLeader && <Crown className="w-3 h-3 text-yellow-500" />}
                      {!isLeader && isAdmin && <Shield className="w-3 h-3 text-purple-500" />}
                      <span className="text-xs font-medium text-gray-700 capitalize">
                        {membership.role.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-4 overflow-x-auto scrollbar-hide">
            {[
              { id: 'feed', label: 'Feed' },
              { id: 'about', label: 'About' },
              { id: 'members', label: 'Members' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 transition-all whitespace-nowrap text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Feed Tab */}
        {activeTab === 'feed' && (
          <div className="space-y-4">
            {/* Post Composer */}
            {isMember && membership?.canPost && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    {userProfile?.profilePicture ? (
                      <img
                        src={userProfile.profilePicture}
                        alt="You"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-sm">
                        {user?.email?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      placeholder="Share something with the group..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows={3}
                    />
                  </div>
                </div>

                {imagePreview && (
                  <div className="relative mb-3">
                    <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <ImageIcon className="w-5 h-5 text-gray-600" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={handleCreatePost}
                    disabled={!newPost.trim() || posting}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {posting ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Post</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Posts List */}
            {posts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Posts Yet</h3>
                <p className="text-sm text-gray-600">
                  {isMember ? 'Be the first to post in this group!' : 'Join the group to see posts'}
                </p>
              </div>
            ) : (
              posts.map((post) => {
                const author = getAuthorDisplay(post.authorId);
                const isOwnPost = post.authorId === user?.uid;

                return (
                  <div key={post.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          {author.avatar ? (
                            <img
                              src={author.avatar}
                              alt={author.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold text-sm">
                              {author.name[0]?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{author.name}</h4>
                          <p className="text-xs text-gray-500">
                            {post.createdAt ? formatDistanceToNow(post.createdAt, { addSuffix: true }) : 'Just now'}
                          </p>
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => setShowPostMenu(showPostMenu === post.id ? null : post.id)}
                          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>
                        {showPostMenu === post.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-10">
                            {isOwnPost || isAdmin ? (
                              <button
                                onClick={() => handleDeletePost(post.id)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete Post</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  alert('Report functionality coming soon');
                                  setShowPostMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                              >
                                <Flag className="w-4 h-4" />
                                <span>Report Post</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Post Content */}
                    <p className="text-gray-800 mb-3 leading-relaxed">{post.content}</p>

                    {/* Post Image */}
                    {post.imageUrl && (
                      <div className="mb-3">
                        <img
                          src={post.imageUrl}
                          alt="Post"
                          className="w-full h-64 object-cover rounded-xl"
                        />
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center space-x-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleLikePost(post.id)}
                        className="flex items-center space-x-2 text-gray-600 hover:text-red-500 transition-colors"
                      >
                        <Heart className="w-5 h-5" />
                        <span className="text-sm font-medium">{post.likeCount || 0}</span>
                      </button>
                      <button className="flex items-center space-x-2 text-gray-600 hover:text-blue-500 transition-colors">
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{post.commentCount || 0}</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">About</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{group.description}</p>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Category</p>
                  <p className="font-semibold text-gray-900">{group.category}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Privacy</p>
                  <p className="font-semibold text-gray-900 capitalize">{group.privacy}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Members</p>
                  <p className="font-semibold text-gray-900">{group.memberCount}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Posts</p>
                  <p className="font-semibold text-gray-900">{group.postCount || 0}</p>
                </div>
              </div>
            </div>

            {group.rules && group.rules.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Group Rules</h2>
                <div className="space-y-3">
                  {group.rules.map((rule, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                      <p className="text-sm text-gray-700 flex-1 pt-0.5">{rule}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isMember && !isAdmin && (
              <button
                onClick={leaveGroup}
                className="w-full px-4 py-3 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
              >
                Leave Group
              </button>
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-3">
            {members.map((member) => {
              const profile = memberProfiles[member.userId];
              const isLeaderMember = member.role === 'leader';
              const isCoAdmin = member.role === 'co_admin';

              return (
                <div key={member.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      {profile?.profilePicture ? (
                        <img
                          src={profile.profilePicture}
                          alt={profile.displayName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold">
                          {profile?.displayName?.[0]?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate flex items-center space-x-2">
                        <span>
                          {profile?.showRealName ? profile.displayName : 'Anonymous Hero'}
                        </span>
                        {isLeaderMember && <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                        {isCoAdmin && <Shield className="w-4 h-4 text-purple-500 flex-shrink-0" />}
                      </h4>
                      <p className="text-sm text-gray-600">{profile?.responderType || 'First Responder'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

